import { LoginTicket, OAuth2Client, TokenPayload } from "google-auth-library";
import { AccessTokenResponse } from "google-auth-library/build/src/auth/oauth2client";

import {
  Code,
  GoogleOAuthUser,
  googleOAuthUserSchema,
  tokenPayloadSchema,
} from "@/types/services";
import { handleGoogleOAuth2Error } from "@/errors/services";
import type { ErrorContext } from "@/errors";
import { ExceptionType } from "@/errors";

export class GoogleOAuthClient {
  private client: OAuth2Client;
  private clientId: string;

  constructor(
    private readonly googleClientId: string,
    private readonly googleClientSecret: string,
    private readonly callbackUrl: string,
    private readonly accessToken?: string,
    private readonly refreshToken?: string,
  ) {
    if (!googleClientId) throw new Error("Google Client ID is missing");
    if (!googleClientSecret) throw new Error("Google Client Secret is missing");
    if (!callbackUrl) throw new Error("Google OAuth Callback URL is missing");
    const client = new OAuth2Client({
      clientId: googleClientId,
      clientSecret: googleClientSecret,
      redirectUri: callbackUrl,
      forceRefreshOnFailure: false,
    });

    if (process.env.GOOGLE_CLASSROOM_DEV_MODE === "true" && accessToken) {
      client.refreshHandler = async (): Promise<AccessTokenResponse> => ({
        access_token: accessToken,
        expiry_date: new Date().getTime() + 1000 * 60 * 60, // now + 1 hour,
      });
    }

    if (accessToken || refreshToken)
      client.setCredentials({
        access_token: accessToken,
        refresh_token: refreshToken,
      });
    this.client = client;
    this.clientId = googleClientId;
  }

  /**
   * Returns the URL that the user should be redirected to in order to connect their Google account
   */
  public getAuthenticateUrl(
    loginHint?: string,
    subscribeToNewsletter: boolean = false,
    usePupilScopes: boolean = false,
  ): string {
    try {
      const teacherScopes: string[] = [
        "https://www.googleapis.com/auth/userinfo.profile",
        "https://www.googleapis.com/auth/userinfo.email",
        "https://www.googleapis.com/auth/classroom.courses.readonly",
        "https://www.googleapis.com/auth/classroom.addons.student",
        "https://www.googleapis.com/auth/classroom.addons.teacher",
        "https://www.googleapis.com/auth/classroom.coursework.students",
      ];
      const pupilScopes: string[] = [
        "https://www.googleapis.com/auth/userinfo.profile",
        "https://www.googleapis.com/auth/userinfo.email",
        "https://www.googleapis.com/auth/classroom.coursework.me",
        "https://www.googleapis.com/auth/classroom.addons.student",
      ];
      const scope: string[] = usePupilScopes ? pupilScopes : teacherScopes;

      const state = JSON.stringify({ subscribeToNewsletter });

      return this.client.generateAuthUrl({
        access_type: "offline",
        include_granted_scopes: true,
        scope,
        loginHint,
        state,
      });
    } catch (error) {
      const errorContext: ErrorContext = {
        operation: "getAuthenticateUrl",
        service: ExceptionType.GoogleOAuth,
        metadata: {
          message: "Failed to get authenticate url",
        },
      };
      return handleGoogleOAuth2Error(error, errorContext);
    }
  }

  /**
   * Returns user info and auth tokens by exchanging a Google OAuth
   * @param code - Code supplied by Google from OAuth callback redirection
   */
  public async exchangeCodeForTokens(
    code: Code,
  ): Promise<GoogleOAuthUser | null> {
    try {
      const { tokens } = await this.client.getToken(code);

      // Verify the tokens we received.
      const ticket: LoginTicket = await this.client.verifyIdToken({
        idToken: tokens.id_token!,
        audience: this.clientId,
      });

      // After verifying the token, we can access the payload.
      const info: TokenPayload | undefined = ticket.getPayload();

      const response = {
        accessToken: tokens.access_token,
        loginHint: info?.sub,
        refreshToken: tokens.refresh_token!, // only received if first time signing in
        profilePictureUrl: info?.picture,
        email: info?.email,
      };

      const parsedResponse = googleOAuthUserSchema.parse(response);

      return parsedResponse;
    } catch (error) {
      const errorContext: ErrorContext = {
        operation: "exchangeCodeForTokens",
        service: ExceptionType.GoogleOAuth,
        metadata: {
          message: "Failed to exchange code for tokens",
        },
      };
      return handleGoogleOAuth2Error(error, errorContext);
    }
  }

  /**
   * Attempts to generate a new access token from a supplied refresh token and returns it
   * @param refreshToken - A Google OAuth 2.0 refresh token
   */
  public async refreshAccessToken(refreshToken?: string): Promise<string> {
    try {
      this.client.setCredentials({ refresh_token: refreshToken });
      const accessToken = await this.client.getAccessToken();
      const parsedAccessToken = tokenPayloadSchema.parse(accessToken);
      return parsedAccessToken.token;
    } catch (error) {
      const errorContext: ErrorContext = {
        operation: "refreshAccessToken",
        service: ExceptionType.GoogleOAuth,
        metadata: {
          message: "Failed to refresh access token",
        },
      };
      return handleGoogleOAuth2Error(error, errorContext);
    }
  }

  /**
   * Checks if the access token attached to the auth client exists and has not expired.
   * (The access token being tested is optionally supplied when the GoogleOAuthClient is constructed)
   */
  public async isAuthenticated(accessToken: string): Promise<boolean> {
    try {
      const tokenInfo = await this.client.getTokenInfo(accessToken);
      const tokenHasExpired = tokenInfo.expiry_date < Date.now();
      if (tokenHasExpired) return false;

      return true;
    } catch (error) {
      const errorContext: ErrorContext = {
        operation: "isAuthenticated",
        service: ExceptionType.GoogleOAuth,
        metadata: {
          message: "Failed to check if access token is authenticated",
        },
      };
      return handleGoogleOAuth2Error(error, errorContext);
    }
  }

  public async getOAuth2Client(): Promise<OAuth2Client> {
    return this.client;
  }
}
